import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import {
	FieldPath,
	FieldValues,
	useController,
	useFormContext,
} from 'react-hook-form';

import CountryFlag from 'library/components/controls/display/CountryFlag';
import {
	ControlledAutocomplete,
	ControlledAutocompleteProps,
	defaultGetOptionLabel,
} from 'library/components/controls/form/ControlledAutocomplete';
import { Option } from 'library/models/Option';

const getOptionCountryCode = ({ id }: Option<string>) => id ?? '';

export type CountryInputProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
	ControlledAutocompleteProps<Option<string>, TFieldValues, TName>,
	'renderOption' | 'options' | 'multiple'
> & {
	options?: Option<string>[];
	withFlag?: boolean;
};

export const CountryInput = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	getOptionLabel = defaultGetOptionLabel,
	getOptionId,
	name,
	options,
	placeholder,
	withFlag = false,
	...props
}: CountryInputProps<TFieldValues, TName>) => {
	const { control } = useFormContext<TFieldValues>();
	const {
		field: { value },
	} = useController({
		name,
		control,
	});

	return (
		<ControlledAutocomplete
			// force single selection
			options={options || []}
			getOptionLabel={getOptionLabel}
			getOptionId={getOptionId}
			name={name}
			control={control}
			renderOption={({ key: _key, ...innerProps }, option) => (
				<Box
					component="li"
					key={(getOptionId ?? getOptionCountryCode)(option)}
					{...innerProps}
					sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<CountryFlag countryCode={getOptionCountryCode(option)} />
					{getOptionLabel(option)}
				</Box>
			)}
			placeholder={placeholder}
			renderInputParams={
				(withFlag && value
					? {
							slotProps: {
								input: {
									startAdornment: (
										<CountryFlag
											countryCode={value}
											sx={{ display: 'inline-flex' }}
										/>
									),
								},
							},
						}
					: {}) as AutocompleteRenderInputParams
			}
			{...props}
		/>
	);
};
